import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame } from "lucide-react";
import { MENU } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const CATS = Object.keys(MENU);

export default function MenuSection() {
  const [active, setActive] = useState(CATS[0]);
  const items = MENU[active];

  return (
    <section id="menu" className="bg-charcoal text-cream py-28 md:py-40 relative grain overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <p className="text-terracotta text-xs uppercase tracking-[0.25em] mb-5">Our Menu</p>
            <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.9]">Made fresh,<br /><em className="text-terracotta not-italic">all day</em>.</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c} data-testid={`menu-tab-${c.toLowerCase()}`} onClick={() => setActive(c)}
                className={`px-5 py-2.5 rounded-full text-sm border transition-colors duration-300 ${
                  active === c ? "bg-terracotta border-terracotta text-cream" : "border-cream/25 text-cream/70 hover:border-cream/60"
                }`}>{c}</button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}
            className="grid md:grid-cols-2 gap-x-16 gap-y-3">
            {items.map((item, i) => (
              <motion.div key={item.name} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.06 }}
                data-testid={`menu-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex gap-5 py-6 border-b border-cream/10 hover:translate-x-1.5 transition-transform duration-300">
                {item.img && (
                  <div className="hidden sm:block h-20 w-20 shrink-0 overflow-hidden rounded-[2px]">
                    <img src={item.img} alt={item.name} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-baseline">
                    <h3 className="font-serif-display text-2xl text-cream flex items-center gap-2">
                      {item.name}
                      {item.popular && <Flame size={15} className="text-terracotta" />}
                    </h3>
                    <span className="leaders" />
                    <span className="font-serif-display text-xl text-terracotta">${Number(item.price).toFixed(2)}</span>
                  </div>
                  <p className="text-cream/55 text-sm mt-1.5 leading-relaxed">{item.desc}</p>
                  {item.popular && <span className="inline-block mt-2 text-[10px] uppercase tracking-[0.2em] text-terracotta">Guest Favorite</span>}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="mt-16 text-center">
          <button onClick={() => scrollToId("order")} data-testid="menu-order-btn"
            className="inline-flex bg-terracotta text-cream px-8 py-4 rounded-full text-sm hover:bg-cream hover:text-ink transition-colors duration-300 active:scale-95">
            Order Your Favorites
          </button>
        </div>
      </div>
    </section>
  );
}
